import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import { setSafeModeActive } from "../lib/calculations";

// ─── Safe Mode Context ──────────────────────────────────────────────────────
// Provides a temporary "presentation privacy" toggle that masks all
// dollar values across the app. Designed for screen-sharing scenarios
// where a user with financial access needs to hide sensitive data
// from on-screen viewers (builders, subcontractors, clients).
//
// State is persisted in localStorage so it survives page refreshes
// during meetings. Keyboard shortcut: ⌘⇧S (Mac) / Ctrl+Shift+S.

const STORAGE_KEY = "claims-safe-mode";

interface SafeModeContextType {
  isSafeMode: boolean;
  toggleSafeMode: () => void;
}

const SafeModeContext = createContext<SafeModeContextType | undefined>(
  undefined
);

export function SafeModeProvider({ children }: { children: ReactNode }) {
  const [isSafeMode, setIsSafeMode] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  // Sync module-level flag synchronously during render so that
  // formatCurrency sees the correct value when children render.
  setSafeModeActive(isSafeMode);

  const toggleSafeMode = useCallback(() => {
    setIsSafeMode((prev) => {
      const next = !prev;
      setSafeModeActive(next);
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // localStorage unavailable, safe mode still works for this session
      }
      return next;
    });
  }, []);

  // ── Keyboard shortcut: ⌘⇧S / Ctrl+Shift+S ──────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "s") {
        e.preventDefault();
        toggleSafeMode();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [toggleSafeMode]);

  return (
    <SafeModeContext.Provider value={{ isSafeMode, toggleSafeMode }}>
      {children}
    </SafeModeContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- context hook
export function useSafeMode(): SafeModeContextType {
  const context = useContext(SafeModeContext);
  if (context === undefined) {
    throw new Error("useSafeMode must be used within a SafeModeProvider");
  }
  return context;
}
